import type { Notebook, Tag } from "@engram/core";
import { Book, BookCopy, Calendar, Check, ChevronLeft, ChevronRight, FileSearch, Search, Tag as TagIcon, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "../db/client";
import { collator } from "./format";
import { Popover } from "./Menu";
import {
  CONTAINS_OPTIONS, DATE_PRESETS, NO_FILTERS, dateLabel, dateRange, filterCount, type ListContext, type ListFilters,
} from "./queries";

type Section = "tags" | "in" | "created" | "updated" | "has";

function useNames() {
  const tags = useQuery<Tag>("SELECT * FROM tags WHERE deleted = 0", [], ["tags"]);
  const notebooks = useQuery<Notebook>("SELECT * FROM notebooks WHERE deleted = 0", [], ["notebooks"]);
  return { tags: tags ?? [], notebooks: notebooks ?? [] };
}

/** Evernote's filter panel: pick a facet, then its values. */
export function FilterPopover({ anchor, ctx, filters, onChange, onClose }: { anchor: HTMLElement; ctx: ListContext; filters: ListFilters; onChange: (f: ListFilters) => void; onClose: () => void }) {
  const [section, setSection] = useState<Section | null>(null);
  const { tags, notebooks } = useNames();
  const facets: [Section, string, React.ReactNode, string | null][] = [
    ["tags", "Tags", <TagIcon size={14} />, filters.tags.length ? String(filters.tags.length) : null],
    ...(ctx.kind !== "notebook"
      ? ([["in", "Located in", <Book size={14} />, filters.in ? (notebooks.find((n) => n.id === filters.in)?.name ?? "") : null]] as [Section, string, React.ReactNode, string | null][])
      : []),
    ["created", "Created", <Calendar size={14} />, filters.created ? dateLabel(filters.created) : null],
    ["updated", "Updated", <Calendar size={14} />, filters.updated ? dateLabel(filters.updated) : null],
    ["has", "Contains", <FileSearch size={14} />, filters.has.length ? String(filters.has.length) : null],
  ];

  return (
    <Popover anchor={anchor} onClose={onClose} align="end" className="menu filter-menu">
      {section === null ? (
        <>
          <div className="menu-heading">Filter notes</div>
          {facets.map(([k, label, icon, value]) => (
            <button key={k} className="menu-item" onClick={() => setSection(k)}>
              <span className="menu-icon">{icon}</span>
              <span className="menu-label">{label}</span>
              {value && <span className="menu-hint filter-value">{value}</span>}
              <ChevronRight size={14} className="muted" />
            </button>
          ))}
          {filterCount(filters) > 0 && (
            <>
              <div className="menu-sep" />
              <button className="menu-item" onClick={() => onChange(NO_FILTERS)}>
                <span className="menu-icon"><X size={14} /></span>
                <span className="menu-label">Clear filters</span>
              </button>
            </>
          )}
        </>
      ) : (
        <>
          <button className="menu-item filter-back" onClick={() => setSection(null)}>
            <span className="menu-icon"><ChevronLeft size={14} /></span>
            <span className="menu-label">{facets.find(([k]) => k === section)?.[1]}</span>
          </button>
          <div className="menu-sep" />
          {section === "tags" && (
            <PickList
              items={[...tags].sort((a, b) => collator.compare(a.name, b.name)).map((t) => ({ id: t.id, label: t.name, icon: <TagIcon size={13} /> }))}
              checked={(id) => filters.tags.includes(id)}
              onPick={(id) => onChange({ ...filters, tags: filters.tags.includes(id) ? filters.tags.filter((x) => x !== id) : [...filters.tags, id] })}
              placeholder="Find tags…"
              empty="No tags yet"
            />
          )}
          {section === "in" && (
            <PickList
              items={[...notebooks]
                .sort((a, b) => collator.compare(a.name, b.name))
                .map((n) => ({ id: n.id, label: n.name, icon: n.kind === "stack" ? <BookCopy size={13} /> : <Book size={13} />, hint: n.kind === "stack" ? "Stack" : undefined }))}
              checked={(id) => filters.in === id}
              onPick={(id) => {
                onChange({ ...filters, in: filters.in === id ? null : id });
                setSection(null);
              }}
              placeholder="Find notebooks…"
              empty="No notebooks"
            />
          )}
          {(section === "created" || section === "updated") && (
            <DatePick
              value={filters[section]}
              onPick={(v) => {
                onChange({ ...filters, [section]: v });
                setSection(null);
              }}
            />
          )}
          {section === "has" &&
            CONTAINS_OPTIONS.map(([k, label]) => (
              <button key={k} className="menu-item" onClick={() => onChange({ ...filters, has: filters.has.includes(k) ? filters.has.filter((x) => x !== k) : [...filters.has, k] })}>
                <span className="menu-icon">{filters.has.includes(k) && <Check size={14} />}</span>
                <span className="menu-label">{label}</span>
              </button>
            ))}
        </>
      )}
    </Popover>
  );
}

function PickList({ items, checked, onPick, placeholder, empty }: { items: { id: string; label: string; icon: React.ReactNode; hint?: string }[]; checked: (id: string) => boolean; onPick: (id: string) => void; placeholder: string; empty: string }) {
  const [q, setQ] = useState("");
  const list = items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      {items.length > 8 && (
        <label className="find-input filter-find">
          <Search size={13} />
          <input autoFocus placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && list[0] && onPick(list[0].id)} />
        </label>
      )}
      <div className="picker-list">
        {list.map((i) => (
          <button key={i.id} className="menu-item" onClick={() => onPick(i.id)}>
            <span className="menu-icon">{checked(i.id) ? <Check size={14} /> : i.icon}</span>
            <span className="menu-label">{i.label}</span>
            {i.hint && <span className="menu-hint">{i.hint}</span>}
          </button>
        ))}
        {list.length === 0 && <div className="menu-empty">{items.length ? "No matches" : empty}</div>}
      </div>
    </>
  );
}

const toInput = (ms: number | null) => (ms == null ? "" : new Date(ms - new Date(ms).getTimezoneOffset() * 60_000).toISOString().slice(0, 10));
const fromInput = (v: string) => v.replaceAll("-", "");

function DatePick({ value, onPick }: { value: string | null; onPick: (v: string | null) => void }) {
  const custom = value != null && !DATE_PRESETS.some(([k]) => k === value);
  const [from0, to0] = custom && value ? dateRange(value) : [null, null];
  const [from, setFrom] = useState(toInput(from0));
  const [to, setTo] = useState(toInput(to0 == null ? null : to0 - 86_400_000));
  return (
    <>
      {DATE_PRESETS.map(([k, label]) => (
        <button key={k} className="menu-item" onClick={() => onPick(value === k ? null : k)}>
          <span className="menu-icon">{value === k && <Check size={14} />}</span>
          <span className="menu-label">{label}</span>
        </button>
      ))}
      <div className="menu-sep" />
      <div className="menu-heading">Custom range</div>
      <form
        className="date-range"
        onSubmit={(e) => {
          e.preventDefault();
          if (from || to) onPick(`${fromInput(from)}..${fromInput(to)}`);
        }}
      >
        <input className="input small" type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className="muted">–</span>
        <input className="input small" type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} />
        <button className="btn small" disabled={!from && !to}>Apply</button>
      </form>
      {value && (
        <button className="menu-item" onClick={() => onPick(null)}>
          <span className="menu-icon"><X size={14} /></span>
          <span className="menu-label">Any time</span>
        </button>
      )}
    </>
  );
}

/** Active filters as removable chips under the list header. */
export function FilterChips({ filters, onChange }: { filters: ListFilters; onChange: (f: ListFilters) => void }) {
  const { tags, notebooks } = useNames();
  if (filterCount(filters) === 0) return null;
  const chips: { key: string; label: string; remove: ListFilters }[] = [
    ...filters.tags.map((t) => ({ key: `t${t}`, label: tags.find((x) => x.id === t)?.name ?? "…", remove: { ...filters, tags: filters.tags.filter((x) => x !== t) } })),
    ...(filters.in ? [{ key: "in", label: `In ${notebooks.find((n) => n.id === filters.in)?.name ?? "…"}`, remove: { ...filters, in: null } }] : []),
    ...(filters.created ? [{ key: "c", label: `Created: ${dateLabel(filters.created)}`, remove: { ...filters, created: null } }] : []),
    ...(filters.updated ? [{ key: "u", label: `Updated: ${dateLabel(filters.updated)}`, remove: { ...filters, updated: null } }] : []),
    ...filters.has.map((h) => ({ key: `h${h}`, label: CONTAINS_OPTIONS.find(([k]) => k === h)?.[1] ?? h, remove: { ...filters, has: filters.has.filter((x) => x !== h) } })),
  ];
  return (
    <div className="filter-chips">
      {chips.map((c) => (
        <span key={c.key} className="chip">
          <span className="chip-text">{c.label}</span>
          <button className="chip-x" aria-label={`Remove filter ${c.label}`} onClick={() => onChange(c.remove)}>
            <X size={11} />
          </button>
        </span>
      ))}
      <button className="link-btn" onClick={() => onChange(NO_FILTERS)}>Clear</button>
    </div>
  );
}
